import React, { useEffect, useState } from 'react';
import { fetchSensors } from '../../services/api';
import { Sensor } from '../../types/types';

interface SensorsSectionProps {
  onSensorSelect?: (key: string) => void;
}

export default function SensorsSection({
  onSensorSelect,
}: SensorsSectionProps) {
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [selectedSensorKey, setSelectedSensorKey] = useState<string>('');
  const [loadingSensors, setLoadingSensors] = useState(true);

  useEffect(() => {
    fetchSensors()
      .then((data) => {
        setSensors(data);
        setSelectedSensorKey(data[0]?.key || '');
      })
      .catch((err) => console.error('❌ Error fetching sensors:', err))
      .finally(() => setLoadingSensors(false));
  }, []);

  return (
    <section className="mb-20">
      <h2
        id="sensors"
        className="scroll-mt-24 text-3xl sm:text-4xl text-center font-extrabold mb-8 tracking-tight"
      >
        <a
          href="#sensors"
          className="text-gray-800 dark:text-gray-200 hover:text-blue-500 no-underline"
        >
          Sensors
        </a>
      </h2>

      <div className="flex md:hidden justify-center mb-6">
        {loadingSensors ? (
          <div className="text-center py-8 text-gray-600 dark:text-gray-300 animate-pulse">
            Loading sensors...
          </div>
        ) : sensors.length === 0 ? (
          <div className="text-center py-8 text-red-500">
            ⚠️ No sensors found!
          </div>
        ) : (
          <select
            value={selectedSensorKey}
            onChange={(e) => {
              const newKey = e.target.value;
              setSelectedSensorKey(newKey);
              onSensorSelect?.(newKey);
            }}
            className="px-4 py-2 w-72 rounded-xl border shadow-sm text-gray-700 dark:text-gray-200
                       dark:bg-gray-700 bg-white focus:ring-2 focus:ring-blue-500 transition-all duration-200"
          >
            {sensors.map((sensor) => (
              <option key={sensor.key} value={sensor.key}>
                {sensor.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="hidden md:block">
        {loadingSensors ? (
          <div className="text-center py-8">Loading sensors...</div>
        ) : sensors.length === 0 ? (
          <div className="text-center py-8 text-red-500">
            ⚠️ No sensors found! Check console for details.
          </div>
        ) : (
          <div className="flex flex-wrap justify-center gap-2 bg-gray-100 dark:bg-gray-800/70 rounded-2xl p-2 max-w-4xl mx-auto mb-6 shadow-inner">
            {sensors.map((sensor) => (
              <button
                key={sensor.key}
                onClick={() => {
                  setSelectedSensorKey(sensor.key);
                  onSensorSelect?.(sensor.key);
                }}
                className={`px-4 py-2 rounded-xl font-semibold transition-all duration-200 ${
                  selectedSensorKey === sensor.key
                    ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-white/60 dark:hover:bg-gray-700/60'
                }`}
              >
                {sensor.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {sensors.length > 0 &&
        selectedSensorKey &&
        (() => {
          const selectedSensor = sensors.find(
            (s) => s.key === selectedSensorKey
          );
          if (!selectedSensor) return null;

          return (
            <div
              className="rounded-xl bg-white/5 dark:bg-gray-800/70 p-4 md:p-6 max-w-4xl mx-auto
                         backdrop-blur-sm shadow-md hover:shadow-lg transition-all duration-300"
            >
              <div className="flex flex-col md:flex-row gap-4 items-start">
                <div className="flex-1">
                  <p className="text-md italic text-gray-600 dark:text-gray-400 leading-relaxed">
                    {selectedSensor.description}
                    {selectedSensor.docLink && (
                      <>
                        {' — '}
                        <a
                          className="text-blue-500 font-semibold hover:underline"
                          target="_blank"
                          rel="noopener noreferrer"
                          href={selectedSensor.docLink}
                        >
                          {selectedSensor.docLabel}
                        </a>
                      </>
                    )}
                  </p>
                </div>

                {selectedSensor.sensor_image && (
                  <div className="flex-shrink-0 mx-auto md:mx-0">
                    <img
                      alt={selectedSensor.name}
                      src={selectedSensor.sensor_image}
                      className="w-48 md:w-60 rounded-md shadow-md object-cover transition-transform duration-300 hover:scale-105"
                    />
                  </div>
                )}
              </div>

              {selectedSensor.file_structure && (
                <>
                  <h3 className="mt-8 text-lg md:text-xl font-bold text-center text-gray-900 dark:text-gray-100">
                    File Structure
                  </h3>
                  <div className="flex flex-col lg:flex-row gap-4 my-4 items-start justify-center">
                    <div className="flex-1">
                      <div
                        className="styled__codeblock relative rounded-md border bg-gray-50 dark:bg-gray-900
                                   p-4 text-left text-sm text-gray-900 dark:text-gray-100 overflow-x-auto shadow-inner"
                      >
                        <pre className="whitespace-pre">
                          {selectedSensor.file_structure.join('\n')}
                        </pre>
                      </div>
                    </div>

                    {selectedSensor.example_data && (
                      <div className="flex-shrink-0">
                        {selectedSensor.key === 'microphones' ? (
                          <audio
                            controls
                            className="w-full max-w-[250px]"
                            preload="metadata"
                          >
                            <source
                              src={selectedSensor.example_data}
                              type="audio/wav"
                            />
                            Your browser does not support the audio element.
                          </audio>
                        ) : (
                          <img
                            alt={selectedSensor.name}
                            src={selectedSensor.example_data}
                            className="w-full max-w-[220px] md:max-w-[250px] object-cover rounded-md"
                          />
                        )}
                      </div>
                    )}
                  </div>
                </>
              )}

              {selectedSensor.namespaces && (
                <>
                  <h3 className="mt-8 text-lg md:text-xl font-bold text-center text-gray-900 dark:text-gray-100">
                    Namespaces
                  </h3>
                  <div
                    className="styled__codeblock relative my-4 rounded-md border bg-gray-50 dark:bg-gray-900
                                p-4 text-left text-sm text-gray-900 dark:text-gray-100 overflow-x-auto shadow-inner max-w-[820px] mx-auto"
                  >
                    <pre className="whitespace-pre">
                      {selectedSensor.namespaces.join('\n')}
                    </pre>
                  </div>
                </>
              )}

              {selectedSensor.details && (
                <>
                  <h3 className="mt-8 text-lg md:text-xl font-bold text-center text-gray-900 dark:text-gray-100">
                    Drivers and Hardware Details
                  </h3>
                  <p className="text-base md:text-lg text-justify text-gray-800 dark:text-gray-200 leading-relaxed mt-2">
                    {selectedSensor.details.join('\n')}
                  </p>
                </>
              )}
            </div>
          );
        })()}
    </section>
  );
}
